"use client"

import { useEffect, useState } from "react"
import { MarketingLink } from "@/components/marketing/marketing-action"
import { MarketingBadge } from "@/components/marketing/marketing-badge"
import { marketingSurfaceClass } from "@/components/marketing/marketing-surface"
import { SectionEyebrow } from "@/components/marketing/section-eyebrow"
import { ScrollToTop } from "@/components/marketing/scroll-to-top"
// Preloads IBM Plex Mono on the legal pages: the "Last updated" line and the
// Contents numbering render in it on the first screen.
import "@/lib/mono-font-preload"

export interface PolicySection {
  id: string
  title: string
  /** A shorter label for the Contents list when the card heading is long.
   *  Falls back to `title`, so every existing policy page renders unchanged. */
  navTitle?: string
  body: React.ReactNode
}

export interface PolicyContact {
  heading: string
  body: React.ReactNode
  ctaHref: string
  ctaLabel: string
}

export interface PolicyPageProps {
  title: string
  subtitle?: string
  lastUpdated?: string
  sections: PolicySection[]
  contact?: PolicyContact
}

export function PolicyPage({
  title,
  subtitle,
  lastUpdated,
  sections,
  contact,
}: PolicyPageProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "")

  useEffect(() => {
    if (sections.length === 0) return

    const updateActiveSection = () => {
      const threshold = window.innerHeight * 0.35
      let currentId = sections[0]?.id ?? ""

      for (const section of sections) {
        const el = document.getElementById(section.id)
        if (!el) continue
        if (el.getBoundingClientRect().top <= threshold) {
          currentId = section.id
        }
      }

      setActiveId(currentId)
    }

    updateActiveSection()
    window.addEventListener("scroll", updateActiveSection, { passive: true })
    window.addEventListener("resize", updateActiveSection)
    return () => {
      window.removeEventListener("scroll", updateActiveSection)
      window.removeEventListener("resize", updateActiveSection)
    }
  }, [sections])

  return (
    <>
      <ScrollToTop />
      <section className="mx-auto max-w-[80rem] px-6 pt-16 lg:px-10 lg:pt-20">
        {lastUpdated ? (
          <MarketingBadge variant="outline" size="md" className="mb-6">
            <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-vermilion)]" />
            Last updated {lastUpdated}
          </MarketingBadge>
        ) : null}
        <h1 className="editorial-heading-md text-[var(--color-ink)]">
          {title}
        </h1>
        {subtitle ? (
          <p className="mt-5 max-w-[44rem] text-[1.1rem] leading-[1.6] text-[var(--color-ink-muted)]">
            {subtitle}
          </p>
        ) : null}
      </section>

      <section className="mx-auto max-w-[80rem] px-6 pb-24 lg:px-10">
        <div className="mt-10 grid grid-cols-1 gap-12 lg:grid-cols-[15rem_1fr] lg:gap-16">
          <aside className="hidden lg:block">
            <div className={marketingSurfaceClass({ className: "sticky top-24 p-5" })}>
              <SectionEyebrow size="sm">Contents</SectionEyebrow>
              <ol className="mt-4 space-y-2">
                {sections.map((s, i) => {
                  const isActive = s.id === activeId
                  return (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={`flex min-h-10 items-center gap-3 text-[0.86rem] leading-snug transition-colors ${
                          isActive
                            ? "font-semibold text-[var(--color-vermilion)]"
                            : "text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
                        }`}
                      >
                        <span
                          className={`tabular-nums shrink-0 font-mono text-[0.76rem] ${
                            isActive ? "text-[var(--color-vermilion)]" : "text-[var(--color-ink-soft)]"
                          }`}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span>{s.navTitle ?? s.title}</span>
                      </a>
                    </li>
                  )
                })}
              </ol>
            </div>
          </aside>

          <div className="max-w-[44rem]">
            <div className="space-y-5">
              {sections.map((s, i) => (
                <section
                  key={s.id}
                  id={s.id}
                  className={marketingSurfaceClass({ className: "scroll-mt-24 p-7 md:p-8" })}
                >
                  <div className="mb-4 flex items-baseline gap-3">
                    <span className="font-mono text-[0.7rem] tabular-nums uppercase tracking-[0.18em] text-[var(--color-ink-soft)]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h2 className="editorial-title-md text-[var(--color-ink)]">
                      {s.title}
                    </h2>
                  </div>
                  <div className="policy-body text-[0.98rem] leading-[1.65] text-[var(--color-ink-muted)]">
                    {s.body}
                  </div>
                </section>
              ))}
            </div>

            {contact ? (
              <div
                className={marketingSurfaceClass({
                  className: "mt-10 border-2 p-7 md:p-8",
                  emphasis: "quiet",
                })}
              >
                <h3 className="editorial-title-md text-[var(--color-ink)]">
                  {contact.heading}
                </h3>
                <p className="policy-body mt-3 text-[0.98rem] leading-[1.6] text-[var(--color-ink-muted)]">
                  {contact.body}
                </p>
                <MarketingLink
                  href={contact.ctaHref}
                  size="md"
                  className="mt-5"
                >
                  {contact.ctaLabel}
                </MarketingLink>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <style jsx global>{`
        .policy-body p { margin: 0 0 0.85rem 0; }
        .policy-body p:last-child { margin-bottom: 0; }
        .policy-body ul { list-style: disc; padding-left: 1.25rem; margin: 0.55rem 0; }
        .policy-body ul li { margin-bottom: 0.4rem; }
        .policy-body a {
          color: var(--color-vermilion);
          text-decoration: underline;
          text-decoration-thickness: from-font;
          text-underline-offset: 0.18em;
          overflow-wrap: anywhere;
        }
        .policy-body a:hover { color: var(--color-oxblood); }
        .policy-body a:focus-visible {
          outline: 2px solid var(--color-vermilion);
          outline-offset: 3px;
        }
        .policy-body strong { color: var(--color-ink); font-weight: 600; }
      `}</style>
    </>
  )
}
